import {useCallback, useEffect, useState} from 'react';
import {QuickWriteState, YClientsFormState} from '@target-web/leadads-form-view';
import {fetchDates, fetchMasters, fetchServices, fetchTimes} from 'utils/requests';
import {useSearchParams} from 'utils/searchParamsContext';
import {useYClientsFormController} from './useYClientsFormController';
import {captureMessage} from '@sentry/react';

type useQuickWriteProps = {
    active?: boolean;
    setQuickWriteState: ReturnType<typeof useYClientsFormController>['setQuickWriteState'];
};

export const useQuickWrite = ({active, setQuickWriteState}: useQuickWriteProps): QuickWriteState => {
    const [isLoading, setIsLoading] = useState(true);
    const {formDataHash} = useSearchParams();

    const setAndReturn = useCallback(
        (result: YClientsFormState['quickWrite'], reason?: string) => {
            if (reason) {
                captureMessage(reason, 'error');
            }
            setQuickWriteState(result);
            setIsLoading(false);
        },
        [setQuickWriteState],
    );

    const getDates = useCallback(async () => {
        setIsLoading(true);

        const result: YClientsFormState['quickWrite'] = {
            services: [],
            datetime: '',
            staffId: null,
        };

        const datesResult = await fetchDates(new URLSearchParams({lead_form_data: formDataHash}).toString());
        const lastAwailableDay = datesResult.data.data.booking_dates.sort((a, b) =>
            new Date(a) > new Date(b) ? 1 : -1,
        )[datesResult.data.data.booking_dates.length - 1];
        if (!lastAwailableDay) {
            return setAndReturn(result, `YClients: not found quick date: ${formDataHash}`);
        }

        const timesResult = await fetchTimes(
            0,
            lastAwailableDay,
            new URLSearchParams({lead_form_data: formDataHash}).toString(),
        );
        if (!timesResult.data.success) {
            return false;
        }
        const lastAwailableDateTime = timesResult.data.data[timesResult.data.data.length - 1].datetime;

        if (!lastAwailableDateTime) {
            return setAndReturn(result, `YClients: not found quick time: ${formDataHash}`);
        }
        result.datetime = lastAwailableDateTime;

        const params = {
            datetime: lastAwailableDateTime.slice(0, 19),
            lead_form_data: formDataHash,
        };

        const servicesResult = await fetchServices(new URLSearchParams(Object.entries(params)).toString());
        const serviceId = servicesResult.data.data.services[0]?.id;

        if (!serviceId) {
            return setAndReturn(result, `YClients: not found quick serviceId: ${formDataHash}`);
        }
        result.services = [serviceId];

        const searchParams = new URLSearchParams({
            ...params,
            'service_ids[]': String(serviceId),
        });

        const masters = await fetchMasters(searchParams.toString());
        const staffId = masters[0]?.id;

        if (!staffId) {
            return setAndReturn(result, `YClients: not found quick staffId: ${formDataHash}`);
        }
        result.staffId = staffId;

        setAndReturn(result);
    }, [formDataHash, setAndReturn]);

    useEffect(() => {
        if (active) {
            getDates();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active]);

    return {
        isLoading,
    };
};
